// Message formatting, Discord style, for everyone's messages and @Bloom's (the parser is shared/src/markdown.ts).
// Built from React elements, never HTML strings, so nothing in a message can inject markup.
// Plain stretches of text go through `text`, which draws @mentions, #channels and bare links as everywhere else.
import { Fragment, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { parseMarkdown, type MdNode, type TimeStyle } from "@bloom/shared";
import { useHighlight } from "../highlight.ts";
import { CustomEmojiView } from "../customEmoji.tsx";

type Render = (text: string) => ReactNode;

/** Hidden until clicked (or Enter/Space), like Discord. While hidden, nothing inside can be clicked, selected or read out. */
function Spoiler({ children }: { children: ReactNode }) {
  const [shown, setShown] = useState(false);
  const reveal = (e: MouseEvent | KeyboardEvent) => {
    if (shown) return;
    e.preventDefault();
    e.stopPropagation();
    setShown(true);
  };
  return (
    <span
      className={`spoiler${shown ? " shown" : ""}`}
      role={shown ? undefined : "button"}
      tabIndex={shown ? undefined : 0}
      aria-label={shown ? undefined : "Spoiler, press to reveal"}
      title={shown ? undefined : "Spoiler"}
      onClickCapture={reveal}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && reveal(e)}
    >
      <span className="spoiler-text" aria-hidden={!shown}>{children}</span>
    </span>
  );
}

function CodeBlock({ code, lang }: { code: string; lang: string | null }) {
  const lit = useHighlight(code, lang);
  return (
    <pre className="md-pre" data-lang={lang ?? undefined}>
      <code className={lit ? "hljs" : undefined}>{lit ?? code}</code>
    </pre>
  );
}

const fmt = {
  t: { hour: "numeric", minute: "2-digit" },
  T: { hour: "numeric", minute: "2-digit", second: "2-digit" },
  d: { year: "numeric", month: "2-digit", day: "2-digit" },
  D: { year: "numeric", month: "long", day: "numeric" },
  f: { year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" },
  F: { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" },
} satisfies Record<Exclude<TimeStyle, "R">, Intl.DateTimeFormatOptions>;

function relative(ts: number): string {
  const s = (ts - Date.now()) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [["year", 31536000], ["month", 2592000], ["day", 86400], ["hour", 3600], ["minute", 60], ["second", 1]];
  const [unit, size] = units.find(([, n]) => Math.abs(s) >= n) ?? ["second", 1];
  return new Intl.RelativeTimeFormat(undefined, { numeric: "auto" }).format(Math.round(s / size), unit);
}

/** A <t:…> timestamp in the reader's own time zone and language, like Discord. */
export function formatStamp(ts: number, style: TimeStyle): string {
  if (!Number.isFinite(ts) || Math.abs(ts) > 8.64e15) return "Invalid date";
  return style === "R" ? relative(ts) : new Intl.DateTimeFormat(undefined, fmt[style]).format(ts);
}

function Stamp({ ts, style }: { ts: number; style: TimeStyle }) {
  const valid = Number.isFinite(ts) && Math.abs(ts) <= 8.64e15;
  return (
    <time className="md-time" dateTime={valid ? new Date(ts).toISOString() : undefined} title={valid ? formatStamp(ts, "F") : undefined}>
      {formatStamp(ts, style)}
    </time>
  );
}

function render(nodes: MdNode[], text: Render, key: string): ReactNode[] {
  return nodes.map((n, i): ReactNode => {
    const k = `${key}.${i}`;
    switch (n.t) {
      case "text":
        return <Fragment key={k}>{text(n.v)}</Fragment>;
      case "strong":
        return <strong key={k}>{render(n.c, text, k)}</strong>;
      case "em":
        return <em key={k}>{render(n.c, text, k)}</em>;
      case "u":
        return <u key={k}>{render(n.c, text, k)}</u>;
      case "s":
        return <s key={k}>{render(n.c, text, k)}</s>;
      case "spoiler":
        return <Spoiler key={k}>{render(n.c, text, k)}</Spoiler>;
      case "code":
        return <code key={k} className="md-code">{n.v}</code>;
      case "codeblock":
        return <CodeBlock key={k} code={n.v} lang={n.lang} />;
      case "quote":
        return <div key={k} className="md-quote">{render(n.c, text, k)}</div>;
      case "heading":
        return <div key={k} className={`md-h md-h${n.level}`} role="heading" aria-level={n.level + 2}>{render(n.c, text, k)}</div>;
      case "subtext":
        return <div key={k} className="md-sub">{render(n.c, text, k)}</div>;
      case "list": {
        const items = n.items.map((it, j) => <li key={j}>{render(it, text, `${k}.${j}`)}</li>);
        return n.ordered ? <ol key={k} className="md-list" start={n.start}>{items}</ol> : <ul key={k} className="md-list">{items}</ul>;
      }
      case "time":
        return <Stamp key={k} ts={n.ts} style={n.style} />;
      case "emoji":
        return <CustomEmojiView key={k} id={n.id} name={n.name} />;
    }
  });
}

/** `open`: the message is still being written (an unclosed ``` shows as code meanwhile). */
export function Markdown({ source, text, open }: { source: string; text: Render; open?: boolean }) {
  return <>{render(parseMarkdown(source, { open }), text, "m")}</>;
}
